/**
 * Growth tiles combine inclusion sources into a small preview. Deduplicate by
 * trimmed description so repeated benefits do not consume slots or inflate +N more.
 * Keep the first source object (including its icon and eligibility metadata) and
 * preserve source order; each vertical owns its business-specific priority.
 */
export function getDistinctGrowthInclusions<T extends Pick<App.OfferInclusion, 'description'>>(inclusions: ReadonlyArray<T>): Array<T> {
  const seen = new Set<string>()
  return inclusions.filter(inclusion => {
    const description = inclusion.description.trim()
    if (!description || seen.has(description)) return false
    seen.add(description)
    return true
  })
}

/**
 * Hotel highlight ordering for the homepage experiment; not a shared offer rule.
 * LEOfferCard.tsx still selects the package/tile inclusions and loyalty benefits.
 * This helper only arranges those items: up to two promotional highlights first,
 * then ordinary inclusions from longest to shortest, as described in Figma:
 * https://www.figma.com/design/IWfV3djmxCiYMSPfzUEEHP?node-id=13188-33915
 *
 * Return all eligible rows so GrowthInclusionList can fill spare carousel height.
 * Promotions beyond the first two still count toward +N more but remain outside
 * the preview. Other verticals retain their existing ordering.
 */
export function getGrowthOfferHighlights(inclusions: ReadonlyArray<App.OfferInclusion>) {
  const distinct = getDistinctGrowthInclusions(inclusions)
  const promotional = distinct.filter(inclusion => inclusion.isHighlighted).slice(0, 2)
  const bullets = distinct.filter(inclusion => !inclusion.isHighlighted)
    .sort((a, b) => b.description.length - a.description.length)
  const ordered = [...promotional, ...bullets]

  return {
    inclusions: ordered,
    // Count every distinct hidden benefit, including promotions beyond the first two.
    additionalInclusionCount: distinct.length - ordered.length,
  }
}

/** Fit whole extra rows, reserving the +N more line until every benefit is visible. */
export function getFittingGrowthInclusionCount(
  availableHeight: number,
  rowHeights: ReadonlyArray<number>,
  moreLineHeight: number,
  gap: number,
  additionalInclusionCount = 0,
): number {
  const fullHeight = rowHeights.reduce((height, rowHeight) => height + rowHeight, 0) + Math.max(0, rowHeights.length - 1) * gap
  if (!additionalInclusionCount && fullHeight <= availableHeight) return rowHeights.length

  let usedHeight = moreLineHeight + gap
  let count = 0
  for (const height of rowHeights) {
    usedHeight += height + gap
    if (usedHeight > availableHeight) break
    count++
  }
  return count
}
